import React from "react";
import { useContext } from "react";

import { css } from "@emotion/core";

import {
  Table,
  TableBody,
  TableHead,
  TableRow,
  TableTd,
  TableTh,
} from "@zapier/design-system";

import { useApolloInjection } from "@zapier/checkout/esm/contexts/ApolloInjection";
import { CurrencyContext } from "@zapier/checkout/esm/contexts/CurrencyContext";
import { PlanTier } from "@zapier/checkout/esm/types";
import { Currency, Price } from "@zapier/currency";

import { useCurrentPlanTiers } from "./hooks/useCurrentPlanTiers";

type MeteredTiersTableProps = {
  /**
   * If not provided, component will retrieve tiers from the user's current plan.
   **/
  tiers?: PlanTier[];
};

const DEFAULT_MAX_DECIMAL = 4;
const FRACTIONAL_PENNY_OFFSET = 2;
const MAX_ALLOWED_DECIMAL_PLACES = 15;

const formatTier = (numTasks: number, tierIndex: number) =>
  numTasks === -1
    ? "Any Additional"
    : `${tierIndex === 0 ? "First" : "Next"} ${numTasks.toLocaleString(
        "en-US"
      )}`;

const calculateMaxDecimals = (price: number) =>
  price % 1 !== 0
    ? Math.min(
        Math.max(
          price.toString().split(".")[1].length + FRACTIONAL_PENNY_OFFSET,
          DEFAULT_MAX_DECIMAL
        ),
        MAX_ALLOWED_DECIMAL_PLACES
      )
    : DEFAULT_MAX_DECIMAL;

const formatPrice = (price: number, currency: Currency) =>
  price === 0 ? (
    "Included"
  ) : (
    <Price currency={currency} maxDecimals={calculateMaxDecimals(price)}>
      {price}
    </Price>
  );

const cell = css`
  background-color: transparent !important;
  font-size: 14px;
  padding: 0.5em 1em !important;
`;

const formatTiers = (data: PlanTier[] = [], currency: Currency) => {
  return data.map((tier, i) => (
    <TableRow key={i}>
      {/* @ts-ignore: Type 'SerializedStyles' is not assignable to type 'string'. */}
      <TableTd className={cell}>{formatTier(tier.numTasks, i)}</TableTd>
      {/* @ts-ignore: Type 'SerializedStyles' is not assignable to type 'string'. */}
      <TableTd className={cell}>
        {formatPrice(tier.pricePerTask, currency)}
      </TableTd>
    </TableRow>
  ));
};

export const MeteredTiersTable = ({ tiers }: MeteredTiersTableProps) => {
  const currency = useContext(CurrencyContext);
  const { useQuery } = useApolloInjection();
  const { currentPlanTiers } = useCurrentPlanTiers({ skip: !!tiers, useQuery });
  const planTiers = tiers ?? currentPlanTiers ?? [];

  const rows = formatTiers(planTiers, currency);

  return planTiers.length > 0 ? (
    <Table kind="standard">
      <TableHead>
        <TableRow>
          {/* @ts-ignore: Type 'SerializedStyles' is not assignable to type 'string'. */}
          <TableTh className={cell}>Tasks</TableTh>
          {/* @ts-ignore: Type 'SerializedStyles' is not assignable to type 'string'. */}
          <TableTh className={cell}>Price Per Task</TableTh>
        </TableRow>
      </TableHead>
      <TableBody>{rows}</TableBody>
    </Table>
  ) : null;
};
